// "Connect Apple Music" — lets an employee link their PERSONAL Apple Music account via
// MusicKit-JS. Renders nothing unless the capability is enabled server-side (flag AND
// credentials AND tenant), so a disabled workspace makes no token/connect requests.
// Personal account + catalog only; no listening data is collected.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Music, Check, Loader2, Link2Off } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { getApiErrorMessage } from '@/lib/apiError';
import { getAppleMusicStatus } from '@/services/performanceService';
import { connectAppleMusicAccount, disconnectAppleMusicAccount } from '@/lib/appleMusic';

export const AppleMusicConnect: React.FC = () => {
  const [enabled, setEnabled] = useState<boolean | null>(null); // null = still discovering
  const [connected, setConnected] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = () =>
    getAppleMusicStatus()
      .then((s) => { setEnabled(s.enabled); setConnected(s.connected); })
      .catch(() => setEnabled(false)); // fail closed: hide on any error

  useEffect(() => { void refresh(); }, []);

  const connect = async () => {
    setBusy(true);
    try {
      await connectAppleMusicAccount();
      await refresh();
      toast.success('Apple Music connected 🎵');
    } catch (e) {
      // A user cancelling Apple's sheet throws too — treat as a quiet no-op.
      const msg = getApiErrorMessage(e, '');
      if (msg) toast.error(getApiErrorMessage(e, msg));
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await disconnectAppleMusicAccount();
      await refresh();
      toast.success('Apple Music disconnected');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not disconnect'));
    } finally { setBusy(false); }
  };

  // Capability off (or still discovering / errored) → render nothing.
  if (!enabled) return null;

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-4">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-foreground/5">
            <Music className="h-5 w-5 text-foreground" />
          </span>
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              Apple Music
              {connected && (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-500/10 px-2 py-0.5 text-[11px] font-medium text-green-600 dark:text-green-400">
                  <Check className="h-3 w-3" />Connected
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {connected
                ? 'Apple Music connected. Your personal library and playback stay private — KaziPeople does not share your listening activity with your employer.'
                : 'Connect your personal Apple Music account to search, play, and build challenges inside KaziPeople. Your library and listening stay private to you.'}
            </p>
          </div>
        </div>
        {connected ? (
          <Button size="sm" variant="outline" disabled={busy} onClick={disconnect}>
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Link2Off className="mr-1.5 h-4 w-4" />}
            Disconnect
          </Button>
        ) : (
          <Button size="sm" disabled={busy} onClick={connect}>
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Music className="mr-1.5 h-4 w-4" />}
            Connect Apple Music
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
